import { init as initCustomerNames } from '../customer-names';
import { init as initCompanyType, rules as companyTypeRules } from '../customer-company-type';
import {
  init as initLoyaltySubscription,
  rules as loyaltySubscriptionRules,
} from './../create-account/create-account-loyalty-subscription';
import { init as initCustomerNationalId } from '../customer-national-id';
import { init as initTooltip } from './../create-account/create-account-tooltip';
import { init as initIdentifier, rules as identifierRules } from './complete-account-identifier';

import * as API from './complete-account-apis';
import { validator, getParameters } from '../customer-utils';
import { i18n, waterfall, isMonoLanguage } from '@/commons/utils';

let personType = 'B2C';
const btobCheckbox = cube.$id('js-btob-checkbox');
if(btobCheckbox && btobCheckbox.checked){
  personType = 'B2B';
}
let personTypeHasChanged = false;

const customRules = {
  rules: {
    subscribeOffers: {
      required: true,
    },

    termsAndConditions: {
      required: true,
    }
  },

  messages: {
    notPracticeSport: {
      required: i18n('validationMsg.required'),
    },
  },
};

// The notPracticeSport checkbox is required only if I have no sport selected
// Unless fieldset has data-optional attribute
const addRuleForSports = () => {
  const field = cube.$id('notPracticeSport');
  const fieldset = field && field.closest('fieldset');
  const selectedSports = cube.$$('.selectable-sports--sport.selected');

  if ((fieldset && fieldset.hasAttribute('data-optional')) || selectedSports.length) {
    field.required = false;
    $(field).valid();
  } else if (field) {
    field.required = true;
  }
};

export const init = form => {
  validator(
    form,
    'input[type="text"]:hidden, #password:hidden, #password-confirm:hidden, #js-address-data:hidden select, #js-optin-data:hidden input, #js-create-account-loyalty-subscription:hidden input, #mobile:hidden, #email:hidden, #email-confirmation:hidden, #js-company-data:hidden #company-type, .bs-searchbox input, #js-personal-data-email:hidden input, #js-personal-data-mobile:hidden input, #js-national-id-block:hidden select',
    customRules,
    identifierRules,
    companyTypeRules,
    loyaltySubscriptionRules
  );

  // No sport checkbox is required only if no sport is selected
  addRuleForSports();
  const observer = new MutationObserver(addRuleForSports);
  const selectedUserSport = cube.$id('user-sports--selected');
  if (selectedUserSport) {
    observer.observe(selectedUserSport, { attributes: true, childList: true, subtree: true });
  }

  initB2B();
  initScrollToPrivacyBlock();
  initSelectPickers();
  initCountryCode();
  initBirthDate();
  initIdentifier(form);

  form.on('submit', completeAccount);

  if (cube.$id('js-remember-me-tooltip')) initTooltip();
  if (cube.$id('js-middlename-checkbox')) initCustomerNames();
  if (cube.$id('js-national-id')) initCustomerNationalId();
  if (cube.$id('js-create-account-loyalty-subscription')) initLoyaltySubscription();
  if (cube.$id('js-address-data')) initAddress();

  const inputText = cube.$$('#js-complete-account-form input[type=text]');
  inputText.forEach((el) => {
    $(el).rules('add', {'emoji': true});
  });

};

// Enable or disable submit button
const disableButton = () => {
  cube.$id('js-complete-account-button').disabled = true;
};
const enableButton = () => {
  cube.$id('js-complete-account-button').disabled = false;
};


const initAddress = () => {
  const checkbox = cube.$id('add-in-address-book-checkbox');
  const addressTitle = cube.$id('js-address-title');

  if (checkbox && addressTitle) checkbox.on('change', e => {
    addressTitle.classList.toggle('hidden', !e.target.checked);
  });
};


// On submit, we call many APIs to update customer data.
const completeAccount = event => {
  event.preventDefault();

  const form = event.target;

  if ($(form).valid()) {
    disableButton();

    const params = getParameters(form);


    /*
     * We must send infos to APIs in a specific order :

      1. If user is a B2C and check B2B checkbox : "transformToCompany"
      2. If user was already a B2B and checkbox is checked : "updateCompany"
      3. Then we send all global requests infos in parallel :
        - "update-complete-account"
        - "updateLoginInformation-mobileValidation"
        - "setUserLoyaltySubscription"
        - "setUserOptin"
      4. If adress block is visible : "setBillingAddress"

      We send all those requests via the "waterfall" method, and
        if all requests succeed, we redirect the user
        else we display an error;
    */

    const requests = [];
    const globalRequests = [];

    // 1. If user is a B2C and check B2B checkbox : "transformToCompany"
    if (personType === 'B2C' && params.btobProcess) {
      personTypeHasChanged = true;
      requests.push(API.transformToCompany(params));
    }

    // 2. If user was already a B2B and checkbox is checked : "updateCompany"
    if (personType === 'B2B' && params.btobProcess && !personTypeHasChanged)
      requests.push(API.updateCompany(params));

    // 3. send all global requests at once
    if (params.firstName)                 globalRequests.push(API.updatePersonalInfos(params));
    if (params.mobile || params.landline) globalRequests.push(API.updateContactInfos(params));
    if (params.loyaltySubscription)       globalRequests.push(API.updateLoyalty(params));
    if (params.subscribeOffers)           globalRequests.push(API.updateNewsletter(params));
    requests.push(globalRequests);

    // 4. If adress block is visible : "setBillingAddress"
    if (cube.$id('js-address-data') && !cube.$id('js-address-data').classList.contains('hidden'))
      requests.push(API.setAddress(params));

    waterfall(requests)
      .then(response => response.reduce((acc, val) => acc.concat(val), []))
      .then(onCompleteSuccess)
      .catch(onCompleteError)
    ;

  } else {
    cube.alert.error(i18n('create_account.validation.error'));
  }
};



// If B2B checkbox is present, show inputs on check
const initB2B = () => {
  const B2Bcheckbox = cube.$id('js-btob-checkbox');
  if (!B2Bcheckbox) return;

  initCompanyType();

  B2Bcheckbox.on('change', () => {
    displayCompanyFields(B2Bcheckbox.checked);
    displayAddressFields(B2Bcheckbox.checked);
  });
};


const displayCompanyFields = isB2B => {
  cube.$id('js-company-data').classList.toggle('hidden', !isB2B);
};


const displayAddressFields = isB2B => {
  const address = cube.$id('js-address-data');
  if (!address) return;

  const showInB2B = address.hasAttribute('data-show-btob-address');
  const showInB2C = address.hasAttribute('data-show-btoc-address');

  if (showInB2C === showInB2B) return;

  if (showInB2B) address.classList.toggle('hidden', !isB2B);
  if (showInB2C) address.classList.toggle('hidden', isB2B);
};

// Init select pickers
const initSelectPickers = () => {
  const selectpicker = cube.$$('.selectpicker');
  if (selectpicker.length) {
    selectpicker.on('change', event => !event.target.closest('.tel-group') && $(event.target).valid());
  }
};

// Scroll to privacy block
const initScrollToPrivacyBlock = () => {
  const privacyLinks = cube.$$('.account-form__subtitle__link');

  if (privacyLinks)
    privacyLinks.on('click', e => {
      const target = cube.$id(e.target.dataset.target) || privacyLinks;
      const header = cube.$('.header-checkout') || cube.$id('header');
      const scrollTop = target.getBoundingClientRect().top + window.pageYOffset - header.offsetHeight - 20;

      $('html, body').animate({ scrollTop }, 500);
    });
};

const initBirthDate = () => {
  const birthdate = cube.$id('js-birthdate');
  if (birthdate) {
    const ATGDate = cube.$id('js-atg-birthdate');
    const date = new Date();
    const { minAge, maxAge } = ATGDate.dataset;
    const value = birthdate.value || null;

    $(birthdate).datetimepicker({
      minDate: new Date(date.getFullYear() - maxAge, date.getMonth(), date.getDate()),
      maxDate: new Date(date.getFullYear() - minAge, date.getMonth(), date.getDate()),
    });

    birthdate.value = value;

    $(birthdate).on('dp.change focusout', () => {
      ATGDate.value = birthdate.value
        ? $(birthdate)
          .data('DateTimePicker')
          .date()
          .format(ATGDate.dataset.dateFormat)
        : null;
    });
  }
};

const initCountryCode = () => {
  const countryCode = cube.$id('countryCodeGeoId');
  if (countryCode) {
    countryCode.on('change', () => cube.form.address.init(countryCode.value, 'BILLING'));
    countryCode.dispatchEvent(new CustomEvent('change'));
  }
};

// If all request succedeed (except setAdress that must be send in last !)
const onCompleteSuccess = responses => {
  const errors = [];

  responses.forEach((response, i) => {
    if (response && response.responseTO && response.responseTO.errors && response.responseTO.errors.length) {
      errors.push(response.responseTO.errors[0]);
      console.error('An error occured with request N°', i, ':', response.responseTO.errors[0]);
    }

    if (response.FragmentResponse && response.FragmentResponse.contents && response.FragmentResponse.contents.length) {
      personType = 'B2C';
    } else {
      personType = 'B2B';
    }
  });

  if (errors.length) {
    cube.alert.error(errors[0].message);
    enableButton();
  }
  else redirect();
};


// If one request failed
const onCompleteError = error => {
  console.error(error);
  cube.alert.error(i18n('completeAccount.error'));
  enableButton();
};


// If every thing is good, we redirect user
const redirect = () => {
  cube.alert.success(i18n('completeAccount.success'));

  const redirectUrl = window.location.search
    .replace('?redirectUrl=/', '');

  setTimeout(() => {
    window.location.href = `${window.location.origin}/${redirectUrl}`;
  }, 3000);
};

/**
 * Disable or enable all form fields except the identifers fields.
 *
 * @param {HTMLFormElement} form The create account form.
 */
export const disableForm = (form, disabled = true) => {
  if (disabled && form.hasAttribute('data-disabled')) return;

  if (disabled) form.dataset.disabled = '';
  else form.removeAttribute('data-disabled');

  const fieldsets = form.$$('.js-fieldset');

  fieldsets.forEach(fieldset => {
    if (disabled) fieldset.classList.add('account-form__section--disabled');
    else fieldset.classList.remove('account-form__section--disabled');

    const fields = [...form.elements].filter(element => element.closest('.js-fieldset'));

    fields.forEach(field => {
      if (disabled && field.disabled) form.dataset.disabled = '';

      if (!disabled && !field.dataset.disabled) field.disabled = disabled;

      if (field.classList.contains('selectpicker')) $(field).selectpicker('refresh');
    });
  });

  disableButton();
};



// WEBPACK FOOTER //
// ./src/customer/complete-account/complete-account.js